import { Link, useNavigate, useRouterState } from '@tanstack/react-router'
import {
  CalendarDays,
  FileText,
  Layers,
  MessageSquarePlus,
  MoreHorizontal,
  Trash2,
  type LucideIcon,
} from 'lucide-react'
import { useMemo, useSyncExternalStore, useEffect } from 'react'
import { useConfirm } from '@/components/ui/confirm'
import { Menu, MenuContent, MenuItem, MenuTrigger } from '@/components/ui/overlays'
import { deleteThread, listThreads, loadThreads, subscribeChat, type Thread } from '@/data/chat'
import { findMeeting } from '@/data/meetings'
import { findSpace } from '@/data/spaces'
import { cn } from '@/lib/utils'

/**
 * No "Chat" entry. The button above IS the way into chat, and the conversation
 * list below is the way back to one — a third control pointing at the same
 * place only raises the question of how it differs from the other two.
 */
const NAV: { to: string; icon: LucideIcon; label: string }[] = [
  { to: '/meetings', icon: CalendarDays, label: 'Meetings' },
  { to: '/templates', icon: FileText, label: 'Templates' },
  { to: '/spaces', icon: Layers, label: 'Spaces' },
]

export function Sidebar() {
  useEffect(() => {
    void loadThreads()
  }, [])

  const location = useRouterState({ select: (state) => state.location.pathname })
  // Exactly `/chat` — a thread at `/chat/:id` is represented by its own row in
  // the list below, so lighting up "New chat" for it would mark two things.
  const composing = location === '/chat'

  return (
    <aside
      className="flex w-64 shrink-0 flex-col border-r border-[var(--color-border)] bg-[var(--color-surface-sunken)]"
      aria-label="Main navigation"
    >
      {/* No scope control here. Scoping is a property of the question being
          asked, so it lives beside the composer — an app-wide selector meant
          every screen silently depended on a control you had to remember the
          state of. */}
      <div className="flex h-12 shrink-0 items-center border-b border-[var(--color-border)] px-3">
        <span className="truncate text-xs font-semibold tracking-tight text-[var(--color-fg)]">
          Nohup Notes
        </span>
      </div>

      <div className="px-2 pt-2 pb-2">
        <Link
          to="/chat"
          aria-current={composing ? 'page' : undefined}
          className={cn(
            'flex h-7 w-full items-center gap-2 rounded-[var(--radius-sm)] px-2 text-xs font-medium',
            'bg-[var(--color-action)] text-[var(--color-action-fg)] hover:bg-[var(--color-action-hover)]',
            composing && 'ring-2 ring-[var(--color-primary)]/35'
          )}
        >
          <MessageSquarePlus className="size-3.5" />
          New chat
        </Link>
      </div>

      <nav className="space-y-px px-2 pb-2">
        {NAV.map((item) => {
          const active = location.startsWith(item.to)
          return (
            <Link
              key={item.to}
              to={item.to}
              className={cn(
                'flex h-7 items-center gap-2 rounded-[var(--radius-sm)] px-2 text-xs',
                active
                  ? 'bg-[var(--color-surface-active)] font-medium text-[var(--color-fg)]'
                  : 'text-[var(--color-fg-muted)] hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-fg)]'
              )}
            >
              <item.icon className="size-3.5 shrink-0" />
              <span className="flex-1 truncate">{item.label}</span>
            </Link>
          )
        })}
      </nav>

      <RecentChats />
    </aside>
  )
}

/**
 * Recent conversations, grouped by when they were started.
 *
 * A flat list answers "which chat was that" by making you read all of them. The
 * buckets are the same ones a calendar uses — today, yesterday, this week —
 * because that is how people actually remember a conversation they had: not by
 * its title, which the first question wrote for them, but by roughly when.
 *
 * The seeded conversations are built by running the live composition over the
 * fixture store (`src/data/chat.ts`), so opening one shows an answer whose
 * citations resolve — which is the only condition under which seeding history
 * is worth doing at all. They live in memory, so anything added here is gone on
 * reload and the list returns to the seeds.
 */
function RecentChats() {
  const confirm = useConfirm()
  const navigate = useNavigate()
  const location = useRouterState({ select: (state) => state.location.pathname })
  // Threads are created and renamed by a run, not by a navigation, so this list
  // has to subscribe rather than re-read on render.
  const threads = useSyncExternalStore(subscribeChat, listThreads)

  const groups = useMemo(() => groupByRecency(threads), [threads])

  if (threads.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 flex-col justify-end border-t border-[var(--color-border)] p-3">
        <p className="text-2xs leading-relaxed text-[var(--color-fg-subtle)]">
          Conversations you start show up here.
        </p>
      </div>
    )
  }

  return (
    <div className="min-h-0 flex-1 overflow-y-auto border-t border-[var(--color-border)] px-2 pb-2">
      {groups.map((group) => (
        <section key={group.label}>
          <p className="px-2 pt-3 pb-1 text-2xs font-medium tracking-[0.08em] text-[var(--color-fg-subtle)] uppercase">
            {group.label}
          </p>
          <ul>
            {group.threads.map((thread) => {
              const active = location.endsWith(thread.id)
              const scope = scopeLabel(thread)
              return (
                <li key={thread.id} className="group relative">
                  <Link
                    to="/chat/$threadId"
                    params={{ threadId: thread.id }}
                    className={cn(
                      'flex h-7 items-center gap-1.5 rounded-[var(--radius-sm)] pr-6 pl-2',
                      active
                        ? 'bg-[var(--color-surface-active)]'
                        : 'hover:bg-[var(--color-surface-hover)]'
                    )}
                    title={scope ? `${thread.title} — ${scope}` : thread.title}
                  >
                    {/* What it was asked against, in the same fixed-width code
                        column the meetings list files by — so scope is read in
                        one vertical pass and an unscoped chat is a gap, not a
                        second line of grey text competing with the date headers
                        above it. The full name is in the row's tooltip. */}
                    <span className="w-6 shrink-0 font-mono text-2xs tracking-tight text-[var(--color-fg-subtle)]">
                      {scopeCode(thread)}
                    </span>
                    <span
                      className={cn(
                        'min-w-0 flex-1 truncate text-xs',
                        active
                          ? 'text-[var(--color-fg)]'
                          : 'text-[var(--color-fg-muted)] group-hover:text-[var(--color-fg)]'
                      )}
                    >
                      {thread.title}
                    </span>
                  </Link>
                  <Menu>
                    <MenuTrigger
                      className="absolute top-1 right-0.5 grid size-5 place-items-center rounded-[var(--radius-xs)] text-[var(--color-fg-subtle)] opacity-0 group-hover:opacity-100 hover:bg-[var(--color-surface-active)] data-[popup-open]:opacity-100"
                      aria-label="Open menu"
                    >
                      <MoreHorizontal className="size-3" />
                    </MenuTrigger>
                    <MenuContent>
                      <MenuItem
                        destructive
                        onClick={async () => {
                          if (await confirm({ title: 'Delete this chat?', tone: 'danger' })) {
                            deleteThread(thread.id)
                            if (active) void navigate({ to: '/chat' })
                          }
                        }}
                      >
                        <Trash2 className="size-3.5" />
                        Delete chat
                      </MenuItem>
                    </MenuContent>
                  </Menu>
                </li>
              )
            })}
          </ul>
        </section>
      ))}
    </div>
  )
}

/**
 * Buckets, newest first, empty ones dropped.
 *
 * Measured against local midnight rather than "24 hours ago": a chat from
 * 11pm last night is yesterday's, not today's, however few hours have passed.
 */
/**
 * The three letters in the scope column, or a blank when the chat was asked
 * against everything.
 *
 * Falls back to the space the picked meetings belong to, so a chat narrowed to
 * one meeting still files under a code rather than reading as unscoped.
 */
function scopeCode(thread: Thread): string {
  // A scope spanning several spaces has no one code to show, and the column is
  // three characters wide — so it says how many rather than picking one.
  if (thread.spaceIds.length > 1) return `${thread.spaceIds.length}◈`
  const direct = findSpace(thread.spaceIds[0] ?? null)
  if (direct) return direct.code
  for (const id of thread.meetingIds) {
    const space = findSpace(findMeeting(id)?.spaceId ?? null)
    if (space) return space.code
  }
  return ''
}

/**
 * The same thing spelled out, for the row's tooltip.
 *
 * A selection is counted rather than listed: naming three meetings makes a
 * tooltip nobody finishes reading.
 */
function scopeLabel(thread: Thread): string | null {
  if (thread.meetingIds.length === 1) {
    const meeting = findMeeting(thread.meetingIds[0]!)
    if (meeting) return meeting.title
  }
  const spaces = thread.spaceIds.flatMap((id) => findSpace(id) ?? [])
  const where =
    spaces.length === 0
      ? null
      : spaces.length === 1
        ? spaces[0]!.name
        : spaces.map((space) => space.code).join(', ')

  if (thread.meetingIds.length > 1) {
    const count = `${thread.meetingIds.length} meetings`
    return where ? `${where} · ${count}` : count
  }
  return where
}

function groupByRecency(threads: Thread[]): Array<{ label: string; threads: Thread[] }> {
  const midnight = new Date()
  midnight.setHours(0, 0, 0, 0)
  const day = 86_400_000

  // Four, not six. A header every second row is not structure, it is noise —
  // and nobody separates a chat from three weeks ago from one from three months
  // ago by reading a label; they scroll.
  const buckets: Array<{ label: string; from: number; threads: Thread[] }> = [
    { label: 'Today', from: midnight.getTime(), threads: [] },
    { label: 'Yesterday', from: midnight.getTime() - day, threads: [] },
    { label: 'Previous 7 days', from: midnight.getTime() - 7 * day, threads: [] },
    { label: 'Earlier', from: -Infinity, threads: [] },
  ]

  for (const thread of threads) {
    const at = new Date(thread.createdAt).getTime()
    buckets.find((bucket) => at >= bucket.from)!.threads.push(thread)
  }

  return buckets
    .filter((bucket) => bucket.threads.length > 0)
    .map(({ label, threads: items }) => ({ label, threads: items }))
}
